// webapp/src/lib/holdBanner.ts
//
// Phase 35 D-01/D-02/D-03 — pure helpers for the hold-release UX.
//
// A "hold" is a `bookings` row with status='held' + held_until = now()+15min
// (set by reserve_slot). The owner can Reprendre (resume checkout) or Libérer
// (release the slot). These helpers select the relevant active hold and format
// its expiry for the banner / dashboard row. Both are pure + deterministic
// (now injectable) so vitest can pin time.
//
// SINGLE SOURCE OF TRUTH: the expired-hold rule is `isHeldExpired` from
// ./adminFilters — NEVER re-inline the held-expiry math (RESEARCH Anti-Pattern
// #1). HoldRow is structurally compatible with isHeldExpired's requirements
// (it reads only `status` + `held_until`), so we cast at the call boundary.

import { isHeldExpired, type CustomBookingRow } from './adminFilters'

// The held-read shape returned by the owner-scoped
// `bookings WHERE status='held'` SELECT (SlotPicker + DashboardSessionsSection).
// Mirrors the columns selected: id, slot_starts_at, slot_ends_at, held_until,
// status. isHeldExpired only reads `status` + `held_until`, so this subset is
// safe to pass through.
export interface HoldRow {
  id: string
  slot_starts_at: string
  slot_ends_at: string
  held_until: string | null
  status: string
}

/**
 * Pick the soonest-expiring NON-expired held row, or null. A row is non-expired
 * iff isHeldExpired(row, now) is false (reuses the shared predicate — no
 * duplicated expiry math). Empty list or all-expired → null. When several holds
 * are still active, the one with the earliest held_until wins (the user should
 * act on the one about to lapse first).
 */
export function selectActiveOwnHold(rows: HoldRow[], now = Date.now()): HoldRow | null {
  // Only held rows qualify. isHeldExpired is the single source of the expiry
  // rule, but it returns false for any non-held status (so a confirmed/cancelled
  // row would otherwise survive the !isHeldExpired filter). Gate on status='held'
  // first so the helper is correct even if the caller passes mixed-status rows.
  const active = rows.filter(
    (r) => r.status === 'held' && !isHeldExpired(r as unknown as CustomBookingRow, now),
  )
  if (active.length === 0) return null
  return active.reduce((soonest, r) => {
    const a = Date.parse(r.held_until ?? '')
    const b = Date.parse(soonest.held_until ?? '')
    // NaN guards: a held row passing isHeldExpired has a parseable held_until,
    // but be defensive — treat unparseable as +Infinity so it never wins.
    const av = Number.isNaN(a) ? Number.POSITIVE_INFINITY : a
    const bv = Number.isNaN(b) ? Number.POSITIVE_INFINITY : b
    return av < bv ? r : soonest
  })
}

/**
 * Format a held_until timestamp as "HH:MM" in Europe/Paris (fr-FR, 24h).
 * Invalid / null / empty input → '' (never throws), so the caller can render
 * the banner without a guard.
 */
export function formatHoldExpiry(heldUntil: string | null): string {
  if (!heldUntil || Number.isNaN(Date.parse(heldUntil))) return ''
  return new Date(heldUntil).toLocaleTimeString('fr-FR', {
    timeZone: 'Europe/Paris',
    hour: '2-digit',
    minute: '2-digit',
  })
}
